import cn from 'classnames'
import type {RefObject} from 'react'
import {useEffect, useRef, useState} from 'react'
import {INPUT_CLASS, INTERNATIONAL_DESTINATIONS, getAvailableDestinations} from '../constants'
import type {CalcResult, Country, Destination} from '../types'

const DESTINATION_MODES: Destination[] = ['domestic', 'international']

const ALL_DESTINATIONS = INTERNATIONAL_DESTINATIONS.flatMap((group) =>
  group.options.map((name) => ({name, group: group.group})),
)

interface DestinationSelectorProps {
  destMode: Destination | null
  intlDest: string
  country: Country | string
  rateNote: CalcResult['rateNote']
  hasError?: boolean
  hasDestModeError?: boolean
  errorRef?: RefObject<HTMLDivElement>
  destModeErrorRef?: RefObject<HTMLDivElement>
  onDestModeChange: (mode: Destination) => void
  onIntlDestChange: (dest: string) => void
}

export const DestinationSelector = ({
  destMode,
  intlDest,
  country,
  rateNote,
  hasError,
  hasDestModeError,
  errorRef,
  destModeErrorRef,
  onDestModeChange,
  onIntlDestChange,
}: DestinationSelectorProps) => {
  const [query, setQuery] = useState(intlDest)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const available =
    country && ['de', 'uk', 'at', 'nl'].includes(country) ? getAvailableDestinations(country as Country) : null

  const countryFiltered = available ? ALL_DESTINATIONS.filter((d) => available.has(d.name)) : ALL_DESTINATIONS

  // Sync query text with selected destination
  useEffect(() => {
    setQuery(intlDest)
  }, [intlDest])

  // Close dropdown and reset query on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setDropdownOpen(false)

        if (!countryFiltered.some((d) => d.name === query)) {
          setQuery(intlDest)
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [query, intlDest, countryFiltered])

  const filtered = query
    ? countryFiltered.filter((d) => d.name.toLowerCase().includes(query.toLowerCase()))
    : countryFiltered

  const handleSelect = (name: string) => {
    setQuery(name)
    onIntlDestChange(name)
    setDropdownOpen(false)
  }

  return (
    <div>
      <div className="px-6 py-8">
        <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-grey-450">Destination</p>
        <div className="grid grid-cols-2 gap-2">
          {DESTINATION_MODES.map((mode) => (
            <button
              key={mode}
              onClick={() => onDestModeChange(mode)}
              className={cn(
                'flex items-center justify-center gap-2 rounded-lg border py-[14px] text-12 font-semibold capitalize transition-all',
                destMode === mode
                  ? 'border-green-dark bg-green-dark text-moss-white shadow-card'
                  : 'border-grey-150 bg-grey-120 text-grey-550 hover:border-grey-300 hover:bg-moss-white hover:text-moss-black',
              )}
            >
              {mode}
            </button>
          ))}
        </div>
        {hasDestModeError && (
          <div
            ref={destModeErrorRef}
            className="mt-3 flex items-center gap-2 rounded-lg border border-ui-red-150 bg-ui-red-150 px-4 py-3 text-13 text-ui-red-700"
          >
            Please select a destination type.
          </div>
        )}

        {destMode === 'international' && (
          <div className="mt-4" ref={containerRef}>
            <label className="mb-1 block text-12 font-medium text-grey-550">Country</label>
            <div className="relative">
              <input
                type="text"
                value={query}
                placeholder="Type to search..."
                onChange={(e) => {
                  setQuery(e.target.value)
                  setDropdownOpen(true)

                  if (!e.target.value) {
                    onIntlDestChange('')
                  }
                }}
                onFocus={() => setDropdownOpen(true)}
                className={cn(INPUT_CLASS, hasError && '!border-ui-red-300')}
              />

              {dropdownOpen && filtered.length > 0 && (
                <div className="absolute left-0 top-full z-40 mt-1 max-h-[240px] w-full overflow-y-auto rounded-lg border border-grey-150 bg-moss-white py-1 shadow-elevation-3-floating">
                  {filtered.map((dest) => (
                    <button
                      key={dest.name}
                      type="button"
                      onClick={() => handleSelect(dest.name)}
                      className={cn(
                        'flex w-full items-center justify-between px-3 py-[6px] text-13 transition-colors',
                        dest.name === intlDest
                          ? 'bg-green-dark font-medium text-moss-white'
                          : 'text-moss-black hover:bg-grey-100',
                      )}
                    >
                      <span>{dest.name}</span>
                      <span
                        className={cn('text-[11px]', dest.name === intlDest ? 'text-moss-white/60' : 'text-grey-300')}
                      >
                        {dest.group}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {dropdownOpen && query && filtered.length === 0 && (
                <div className="absolute left-0 top-full z-40 mt-1 w-full rounded-lg border border-grey-150 bg-moss-white p-3 text-13 text-grey-450 shadow-elevation-3-floating">
                  No destinations found
                </div>
              )}
            </div>

            {hasError && (
              <div
                ref={errorRef}
                className="mt-3 flex items-center gap-2 rounded-lg border border-ui-red-150 bg-ui-red-150 px-4 py-3 text-13 text-ui-red-700"
              >
                <span>Please select a travel destination.</span>
              </div>
            )}
            {rateNote && (
              <div className="mt-2 rounded-lg border border-[#c5deff] bg-[#f0f7ff] px-4 py-3 text-12 text-[#2a4a80]">
                {rateNote}
              </div>
            )}
          </div>
        )}
      </div>
      <div className="mx-6 border-b border-grey-150" />
    </div>
  )
}
